import {
    Box,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from "@mui/material";
import { useState } from "react";

import { bcRow, bcTableFrame, bcTd, bcTh } from "@/routes/table-styles";

import { ds } from "../theme/ds";
import type { WidgetProps } from "./types";

// Several tables behind one tab strip, each on the design system's `.bc-table`
// (navy header, gray rules, gray-50 hover): the assortment tiers on the Tvilum
// pitch room (Bill, 2026-09-24). Tabs are styled as the Capabilities page's
// section tabs; the first tab opens by default.
const MAX_ROWS = 20;
const ROW_PX = 41; // 13px text plus the bcTd padding and rule
// The first column (the item name) takes a third of the width; the rest share the remainder.
function colWidth(index: number, count: number) {
    if (count === 1) return "100%";
    return index === 0 ? "34%" : `${66 / (count - 1)}%`;
}
// Both tables (header, body) fill their box at a fixed column layout so they
// line up. Under md the boxes keep a floor of about 100px a column (560px at
// least), which is what the frame scrolls sideways over on a phone: nine fixed
// columns squeezed into 390px overlapped their numbers (Bill, 2026-09-28). The
// floor is a number, never `max-content`: a fixed-layout table's max-content
// resolved to half a million pixels and the frame scrolled into the void.
const TABLE_SX = { width: "100%", tableLayout: "fixed" } as const;
const boxSx = (columns: number) => ({ minWidth: { xs: Math.max(560, columns * 100), md: 0 } });

export function TabbedTable({ widget }: WidgetProps<"tabbed_table">) {
    const { title, tabs } = widget.props;
    const [index, setIndex] = useState(0);
    const tab = tabs[Math.min(index, tabs.length - 1)];

    return (
        <Box>
            {title && (
                <Typography
                    component="div"
                    sx={{ fontSize: 16, fontWeight: 700, color: ds.navy, mb: 1 }}
                >
                    {title}
                </Typography>
            )}
            {/* Underline tabs: the strip carries a gray-200 rule, the active tab a 2px
                navy underline and bold navy label, the others gray-700 with a gray-50
                hover, so they read as clickable tabs (Bill, 2026-09-24). */}
            <Box sx={{ borderBottom: `1px solid ${ds.gray200}`, mb: 2 }}>
                <Tabs
                    value={index}
                    onChange={(_, v: number) => setIndex(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    TabIndicatorProps={{ sx: { height: 2, bgcolor: ds.navy } }}
                >
                    {tabs.map((t) => (
                        <Tab
                            key={t.label}
                            disableRipple
                            label={t.count != null ? `${t.label} (${t.count})` : t.label}
                            sx={{
                                minHeight: 44,
                                px: 2.5,
                                textTransform: "none",
                                fontSize: 14,
                                lineHeight: "20px",
                                fontWeight: 500,
                                color: ds.gray700,
                                opacity: 1,
                                cursor: "pointer",
                                borderRadius: "8px 8px 0 0",
                                "&:hover": { bgcolor: ds.gray50, color: ds.navy },
                                "&.Mui-selected": { color: ds.navy, fontWeight: 700 },
                            }}
                        />
                    ))}
                </Tabs>
            </Box>
            {tab.caption && (
                <Typography variant="body2" sx={{ color: ds.gray700, mb: 2, maxWidth: 760 }}>
                    {tab.caption}
                </Typography>
            )}
            {/* About 20 rows tall, then the frame scrolls with the navy header pinned
                (Bill, 2026-09-24: some tiers run to a hundred SKUs). */}
            {/* The header sits outside the scroll box so the scrollbar starts under it,
                and both tables share a fixed column layout so they line up; the grid
                spans the full width on every tab so switching tabs does not resize it
                (Bill, 2026-09-24). scrollbar-gutter keeps the header's columns aligned
                with a body that reserves space for its scrollbar. */}
            {/* On a phone the grid keeps a width floor (boxSx) and the whole frame
                scrolls sideways, header and body together (Bill, 2026-09-28). */}
            <Box sx={{ ...bcTableFrame, width: "100%", overflowX: { xs: "auto", md: "hidden" } }}>
                {/* Under md both inner boxes carry the width floor so the overflow
                    lands on the frame, which scrolls; at 100% they clipped the header
                    and let the body scroll alone. */}
                <Box
                    sx={{
                        overflow: "hidden",
                        scrollbarGutter: "stable",
                        bgcolor: ds.navy,
                        ...boxSx(tab.columns.length),
                    }}
                >
                    <Table size="small" sx={TABLE_SX}>
                        <colgroup>
                            {tab.columns.map((c, j) => (
                                <col
                                    key={c.label}
                                    style={{ width: colWidth(j, tab.columns.length) }}
                                />
                            ))}
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                {tab.columns.map((c) => (
                                    <TableCell
                                        key={c.label}
                                        sx={{
                                            ...bcTh,
                                            whiteSpace: "normal",
                                            textAlign: c.align ?? "left",
                                        }}
                                    >
                                        {c.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                    </Table>
                </Box>
                <Box
                    sx={{
                        overflowY: "auto",
                        scrollbarGutter: "stable",
                        ...boxSx(tab.columns.length),
                        maxHeight: MAX_ROWS * ROW_PX,
                    }}
                >
                    <Table size="small" sx={TABLE_SX}>
                        <colgroup>
                            {tab.columns.map((c, j) => (
                                <col
                                    key={c.label}
                                    style={{ width: colWidth(j, tab.columns.length) }}
                                />
                            ))}
                        </colgroup>
                        <TableBody>
                            {tab.rows.map((row, i) => (
                                <TableRow key={i} sx={bcRow}>
                                    {row.map((cell, j) => (
                                        <TableCell
                                            key={j}
                                            sx={{
                                                ...bcTd,
                                                textAlign: tab.columns[j]?.align ?? "left",
                                                fontVariantNumeric: "tabular-nums",
                                                ...(j === 0
                                                    ? { fontWeight: 600, color: ds.navy }
                                                    : {}),
                                            }}
                                        >
                                            {cell}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
            {tab.source && (
                <Typography
                    component="div"
                    sx={{ fontSize: ds.text.xs, color: ds.gray500, mt: 1.5 }}
                >
                    Source: {tab.source}
                </Typography>
            )}
        </Box>
    );
}
